import { useState } from 'react'

import { ColorSettings } from '@tribeplatform/react-components/CMS'
import { EntityImage } from '@tribeplatform/react-components/common/components'
import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import {
  checkIconById,
  convertIdToGlyph,
  MediaPicker,
} from '@tribeplatform/react-components/MediaPicker'
import {
  GenericSettings,
  ActionTypes,
  type ActionBarActionItem,
  type ActionVariants,
  type ReactionActionTypes,
} from '@tribeplatform/react-components/Views'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { noop } from '@tribeplatform/react-ui-kit/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { ActionStates } from '../types.js'
import {
  actionStatesFactory,
  actionVariantOptionsFactory,
  reactionTypeOptionsFactory,
} from '../utils/selectItems.js'

interface Props {
  action: ActionBarActionItem
  hasMultiplePostTypes: boolean
  setAction: (action: ActionBarActionItem) => void
  goBack?: () => void
}
export const ActionSettings = ({
  action,
  hasMultiplePostTypes,
  setAction,
  goBack,
}: Props) => {
  const { $t } = useI18n()
  const actionStates = useStaticIntl(actionStatesFactory)
  const actionVariantOptions = useStaticIntl(actionVariantOptionsFactory)
  const reactionTypeOptions = useStaticIntl(reactionTypeOptionsFactory)
  const { type, variant, defaultLabel, glyphId, color } = action
  const {
    activeLabel = defaultLabel,
    activeGlyphId = glyphId,
    activeColor = color,
  } = action
  const [actionState, setActionState] = useState<ActionStates>(
    ActionStates.default,
  )
  const icon = convertIdToGlyph(glyphId)
  const isIcon = checkIconById(glyphId)
  const activeIcon = convertIdToGlyph(activeGlyphId)
  const isActiveIcon = checkIconById(activeGlyphId)

  return (
    <GenericSettings
      title={defaultLabel}
      description={
        <T
          defaultMessage="Customize the look of actions in both their default and active states."
          id="PostsBlock.ActionBar.Actions.Description"
        />
      }
      goBack={goBack}
    >
      <div className="space-y-5">
        {type !== ActionTypes.share && (
          <FormControl.ButtonGroupSelect
            name="reactionState"
            fullWidth
            value={actionState}
            onChange={(actionState: ActionStates) => {
              setActionState(actionState)
            }}
            items={actionStates}
          />
        )}
        {actionState === ActionStates.default && (
          <div className="space-y-5">
            {type === ActionTypes.react && (
              <FormControl.Select
                name="reactionType"
                label={
                  <T
                    id="Generics.ReactionType"
                    defaultMessage="Reaction type"
                  />
                }
                value={action.reactionActionType}
                // disabled={!hasMultiplePostTypes}
                disabled
                onChange={(reactionActionType: ReactionActionTypes) => {
                  // This feature will be implemented soon
                  noop(reactionActionType)
                  noop(hasMultiplePostTypes)
                  // setAction({
                  //   ...action,
                  //   reactionActionType,
                  // })
                }}
                items={reactionTypeOptions}
              />
            )}
            <FormControl.Select
              name="actionVariant"
              label={$t({ defaultMessage: 'Variant', id: 'Generics.Variant' })}
              value={variant}
              onChange={(value: ActionVariants) =>
                setAction({ ...action, variant: value })
              }
              items={actionVariantOptions}
            />
            <FormControl.Input
              name="actionLabel"
              label={<T defaultMessage="Label" id="Generics.Label" />}
              placeholder={$t({
                defaultMessage: 'Label',
                id: 'Generics.Label',
              })}
              helperText={$t({
                defaultMessage:
                  'The label of the action when it is in its default state. Leave empty to use the default label.',
                id: 'PostsBlock.ActionBar.Actions.DefaultLabel.HelperText',
              })}
              value={defaultLabel}
              onChange={e => {
                const { value } = e.target
                setAction({ ...action, defaultLabel: value })
              }}
            />
            <MediaPicker
              value={icon}
              onChange={icon => {
                const glyphId = icon?.id ?? ''
                const isIcon = checkIconById(glyphId)
                const newColor = isIcon ? action.color : null

                setAction({
                  ...action,
                  glyphId,
                  color: newColor,
                  itemIcon: glyphId ? (
                    <EntityImage
                      entity={{
                        image: convertIdToGlyph(glyphId),
                        name: defaultLabel ?? '',
                      }}
                      size="lg"
                      displayColor={color}
                    />
                  ) : (
                    <SvgIcon name="emotion" size="lg" />
                  ),
                })
              }}
              actionTitle={$t({
                defaultMessage: 'Click to select an icon or emoji',
                id: 'PostsBlock.ActionBar.Actions.ClickToSelectIcon',
              })}
              displayColor={color}
              iconStyle="line"
              hasEmoji
              hasIcon
              hasUpload={false}
            />
            {isIcon && (
              <ColorSettings
                color={color}
                setColor={newColor => setAction({ ...action, color: newColor })}
              />
            )}
          </div>
        )}
        {actionState === ActionStates.active && (
          <div className="space-y-5">
            <FormControl.Input
              name="activeLabel"
              label={
                <T defaultMessage="Active label" id="Generics.ActiveLabel" />
              }
              placeholder={$t({
                defaultMessage: 'Active label',
                id: 'Generics.ActiveLabel',
              })}
              helperText={$t({
                defaultMessage:
                  'The label of the action when it is in its active state. If not provided, the default label will be used.',
                id: 'PostsBlock.ActionBar.Actions.ActiveLabel.HelperText',
              })}
              value={activeLabel}
              onChange={e =>
                setAction({ ...action, activeLabel: e.target.value })
              }
            />
            <MediaPicker
              value={activeIcon}
              onChange={icon => {
                const activeGlyphId = icon?.id ?? ''
                const isIcon = checkIconById(activeGlyphId)
                const newActiveColor = isIcon ? action.activeColor : null

                setAction({
                  ...action,
                  activeGlyphId,
                  activeColor: newActiveColor,
                })
              }}
              displayColor={activeColor}
              iconStyle="solid"
              hasEmoji
              hasIcon
              hasUpload={false}
              actionTitle={$t({
                defaultMessage: 'Click to select an icon or emoji',
                id: 'PostsBlock.ActionBar.Actions.ClickToSelectIcon',
              })}
              helperText={$t({
                defaultMessage:
                  'Represents the reaction when in its active state. If not provided, the default icon will be used.',
                id: 'PostsBlock.ActionBar.Actions.ActiveIcon.HelperText',
              })}
            />
            {isActiveIcon && (
              <ColorSettings
                color={activeColor}
                setColor={newColor =>
                  setAction({ ...action, activeColor: newColor })
                }
              />
            )}
          </div>
        )}
      </div>
    </GenericSettings>
  )
}
